<!--  家校训练服务-详情-评估结果-查看已答题目 -->
<template>
  <div class="answer-history">
    <div class="field">
      <div class="field_l">当前评估领域：{{ field || '-' }}</div>
      <div>
        <a-button shape="round" @click="$emit('return')">
          <a-icon type="left" />返回答题
        </a-button>
      </div>
    </div>
    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container">
      <a-spin size="large" />
    </div>

    <!-- 题目列表 -->
    <div v-else-if="historyList.length > 0" class="history-list">
      <div class="history-item" v-for="item in historyList" :key="item.id">
        <div class="question-header">
          <div class="code">
            <span class="type">题目编号：{{ formatCode(item) }}</span>
          </div>
          <div class="title-l">题干：{{ item.questionStem || '--' }}</div>
          <!-- 修改答案按钮 -->
          <a-button type="link" @click="openEditModal(item)">
            <a-icon type="edit" />修改答案
          </a-button>
        </div>

        <!-- 选项摘要展示 -->
        <div class="option-summary">
          <!-- 情况1：score>0 的选项全部选中 -> 展示"以上行为均具备" -->
          <template v-if="item.display.allSelected">
            <a-checkbox :checked="true" disabled>
              以上行为均具备
              <a-icon type="check" :style="{ color: '#3a5ff8' }" />
            </a-checkbox>
          </template>
          <!-- 情况2：score=0 被选中（即"以上行为均不具备"） -> 只展示"以上行为均不具备" -->
          <template v-else-if="item.display.noneSelected">
            <a-checkbox :checked="true" disabled>
              以上行为均不具备
              <a-icon type="close" :style="{ color: '#ff4d4f' }" />
            </a-checkbox>
          </template>
          <!-- 情况3：部分选中 -> 展示选中的选项 -->
          <template v-else>
            <div v-for="option in item.selectedOptionList" :key="option.id" class="selected-option">
              <a-checkbox :checked="true" disabled>{{ option.description }}</a-checkbox>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- 空状态 -->
    <a-empty v-else description="暂无已答题目" />

    <!--播放视频-->
    <Video ref="video"></Video>

    <!-- 修改答案弹窗 -->
    <a-modal
      v-model="editModalVisible"
      title="修改答案"
      :closable="false"
      :maskClosable="false"
      width="800px"
      :footer="null"
      wrapClassName="answer-edit-modal"
    >
      <div v-if="currentEditQuestion" class="edit-modal-content">
        <div class="title">
          <div class="title-l">题干：{{ currentEditQuestion.questionStem || '--' }}</div>
        </div>
        <span
          class="example"
          v-if="currentEditQuestion.instance || (currentEditQuestion.resourceList && currentEditQuestion.resourceList.length)"
        >
          <span
            class="example-l"
            v-if="currentEditQuestion.instance"
          >示例：{{ currentEditQuestion.instance }}</span>
          <a-button
            type="link"
            @click="viewInformation(currentEditQuestion)"
            v-if="currentEditQuestion.resourceList && currentEditQuestion.resourceList.length"
          >{{ getPictureOrVideo(currentEditQuestion) === 'VIDEO' ? '查看视频' : getPictureOrVideo(currentEditQuestion) === 'IMAGE' ? '查看图片' : '' }}</a-button>
        </span>
        <div class="description">
          <span class="type">多选题</span>请在以下选项中，选出符合儿童情况的所有选项
        </div>
        <div class="option">
          <!-- 普通选项 -->
          <a-checkbox-group v-model="currentEditQuestion.selectOptions" style="width: 100%">
            <div
              v-for="(option, index) in currentEditQuestion.optionList"
              class="option-box"
              v-if="option.score != 0 && option.id !== -1"
              :key="index"
              :class="currentEditQuestion.selectOptions.indexOf(option.id) === -1 ? '' : 'active'"
            >
              <a-checkbox
                :value="option.id"
                :disabled="currentEditQuestion.choiceAll || currentEditQuestion.allHave"
              >
                <div
                  class="option-description"
                  :class="currentEditQuestion.selectOptions.indexOf(option.id) === -1 ? '' : 'hot'"
                >{{ option.description }}</div>
              </a-checkbox>
            </div>
          </a-checkbox-group>
          <!-- 最后一行：以上行为均具备 + 以上行为均不具备 -->
          <div class="option-box option-row-last">
            <a-checkbox
              :checked="currentEditQuestion.allHave"
              :disabled="!canAllHave"
              @change="handleAllHaveChange"
            >
              <div class="option-description">
                以上行为均具备
                <a-icon type="check" :style="{ color: '#3a5ff8' }" />
              </div>
            </a-checkbox>
            <!-- 以上行为均不具备 -->
            <a-checkbox :checked="currentEditQuestion.choiceAll" @change="handleNoneOptionChange">
              <div class="option-description">
                以上行为均不具备
                <a-icon type="close" :style="{ color: '#ff4d4f' }" />
              </div>
            </a-checkbox>
          </div>
        </div>

        <!-- 保存按钮 -->
        <div class="modal-footer">
          <a-button @click="editModalVisible = false">暂不修改</a-button>
          <a-button type="primary" @click="saveEditAnswer" :loading="saving">确定修改</a-button>
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script>
import * as planApi from '@/api/plan'
import Video from '@/components/video'
import { mapActions } from 'vuex'

export default {
  name: 'AnswerHistory',
  props: {
    evaluationFieldId: {
      type: [String, Number],
      required: true,
    },
    field: {
      type: String,
      default: '',
    },
  },
  components: {
    Video,
  },
  data() {
    return {
      loading: false,
      historyList: [],
      // 修改答案弹窗
      editModalVisible: false,
      currentEditQuestion: null,
      saving: false,
      // 是否由程序内部修改 selectOptions（避免 watch 循环触发）
      isInternalChange: false,
    }
  },
  created() {
    if (this.evaluationFieldId) {
      this.getHistoryList()
    }
  },
  computed: {
    // 计算是否可以勾选"以上行为均具备"
    canAllHave() {
      return (
        this.currentEditQuestion &&
        this.currentEditQuestion.optionList &&
        this.currentEditQuestion.optionList.some((o) => o && o.score > 0)
      )
    },
  },
  watch: {
    // 监听当前编辑题目的选项变化，自动同步 allHave
    'currentEditQuestion.selectOptions': {
      handler(newVal) {
        if (this.isInternalChange) return
        if (!this.currentEditQuestion) return
        const scoredOptions = this.currentEditQuestion.optionList?.filter((o) => o && o.score > 0) || []
        if (!scoredOptions.length) return

        const allSelected = scoredOptions.every((o) => newVal.includes(o.id))
        this.isInternalChange = true
        if (allSelected) {
          this.currentEditQuestion.allHave = true
          this.currentEditQuestion.choiceAll = false
        } else {
          this.currentEditQuestion.allHave = false
        }
        this.$nextTick(() => {
          this.isInternalChange = false
        })
      },
      deep: true,
    },
  },
  methods: {
    ...mapActions(['changeSpinning']),

    // 格式化题目编号
    formatCode(question) {
      const n = question.code && question.code.slice(8)
      const codeLengthLimit = 3
      return (Array(codeLengthLimit).join(0) + n).slice(-codeLengthLimit)
    },

    // 获取选项描述
    getOptionDescription(question, optionId) {
      const option = question.optionList.find((o) => o && o.id === optionId)
      return option ? option.description : ''
    },

    // 获取已答题目列表
    async getHistoryList() {
      this.loading = true
      this.changeSpinning(true)
      try {
        const res = await planApi.searchAllSubject(this.evaluationFieldId)
        if (res.code === 0) {
          const answerSubjectList = res.data.answerSubjectList || []
          this.historyList = answerSubjectList
            .map((item) => {
              const optionList = item.optionList || []

              // 分离 score>0 的普通选项 和 score=0 的"以上行为均不具备"
              const scoredOptions = optionList.filter((o) => o && o.score > 0)
              const noneOptions = optionList.filter((o) => o && o.score === 0)

              // 以 optionList 里的 isSelect 为数据源，计算出当前选中的选项 id 列表
              const selectedIds = optionList.filter((o) => o && o.isSelect).map((o) => o.id)

              // 当前选中的普通选项（score>0）
              const selectedScoredOptions = scoredOptions.filter((o) => selectedIds.includes(o.id))

              // 是否选中了"以上行为均不具备"（score=0）
              const noneSelected = noneOptions.some((o) => o && o.isSelect)

              // score>0 的选项是否全部被选中
              const allSelected = scoredOptions.length > 0 && scoredOptions.every((o) => selectedIds.includes(o.id))

              // 供弹窗编辑使用：把选中的选项 id（含 score=0 的）放进 selectOptions
              const selectOptions = optionList.filter((o) => o && o.isSelect).map((o) => o.id)

              return {
                ...item,
                optionList,
                selectOptions,
                choiceAll: noneSelected,
                // 列表摘要展示用
                selectedOptionList: selectedScoredOptions,
                display: {
                  allSelected,
                  noneSelected,
                },
              }
            })
            .sort((a, b) => a.code - b.code) // 按题目编号升序排序
        } else {
          this.$notification['error']({
            message: '提示',
            description: res.msg || '获取历史数据失败',
          })
        }
      } catch (err) {
        this.$notification['error']({
          message: '提示',
          description: '获取历史数据失败',
        })
      } finally {
        this.loading = false
        this.changeSpinning(false)
      }
    },

    // 打开修改答案弹窗
    openEditModal(question) {
      this.isInternalChange = true
      this.currentEditQuestion = JSON.parse(JSON.stringify(question))
      // 初始化 allHave 状态：选中的普通选项是否覆盖了全部普通选项
      const scoredOptions = this.currentEditQuestion.optionList?.filter((o) => o.score > 0) || []
      const selectOptions = this.currentEditQuestion.selectOptions || []
      this.currentEditQuestion.allHave =
        scoredOptions.length > 0 && scoredOptions.every((o) => selectOptions.includes(o.id))
      this.editModalVisible = true
      this.$nextTick(() => {
        this.isInternalChange = false
      })
    },

    // 获取媒体类型
    getPictureOrVideo(question) {
      const { resourceList } = question
      if (resourceList && resourceList.length !== 0) {
        return resourceList[0].type
      }
      return false
    },

    // 查看资料
    viewInformation(question) {
      const { resourceList } = question
      if (resourceList && resourceList.length !== 0) {
        const { type, url } = resourceList[0]
        if (type === 'VIDEO') {
          if (this.$refs.video) {
            this.$refs.video.showModal({ teachingVideo: { url } })
          }
        }
        if (type === 'IMAGE') {
          const urls = resourceList.map((item) => item.url)
          this.$viewerApi({
            images: urls,
          })
        }
      }
    },

    // 处理"以上行为均具备"勾选变化
    handleAllHaveChange(e) {
      const checked = e.target.checked
      this.isInternalChange = true
      this.currentEditQuestion.allHave = checked
      if (checked) {
        // 选"以上行为均具备"：取消"均不具备"，并选中全部普通选项
        this.currentEditQuestion.choiceAll = false
        const scoredOptions = this.currentEditQuestion.optionList.filter((o) => o.score > 0)
        this.currentEditQuestion.selectOptions = scoredOptions.map((o) => o.id)
      } else {
        // 取消勾选：清空普通选项
        this.currentEditQuestion.selectOptions = []
      }
      this.$nextTick(() => {
        this.isInternalChange = false
      })
    },

    // 处理"以上行为均不具备"变化
    handleNoneOptionChange(e) {
      const checked = e.target.checked
      this.isInternalChange = true
      this.currentEditQuestion.choiceAll = checked
      if (checked) {
        // 选"以上行为均不具备"：取消"均具备"，并放入 score=0 的选项 id
        this.currentEditQuestion.allHave = false
        const noneOption = this.currentEditQuestion.optionList.find((o) => o && o.score === 0)
        this.currentEditQuestion.selectOptions = noneOption ? [noneOption.id] : []
      } else {
        // 取消勾选：清空
        this.currentEditQuestion.selectOptions = []
      }
      this.$nextTick(() => {
        this.isInternalChange = false
      })
    },

    // 保存修改后的答案
    async saveEditAnswer() {
      if (!this.currentEditQuestion) return

      const { id: subjectId, selectOptions: optionIdList } = this.currentEditQuestion
      if (!optionIdList || optionIdList.length === 0) {
        this.$notification['error']({ message: '提示', description: '请选择答案' })
        return
      }

      this.saving = true
      try {
        const params = {
          optionIdList,
          evaluationFieldId: this.evaluationFieldId,
          subjectId,
        }
        const res = await planApi.answer(params)
        if (res.code === 0) {
          this.$notification['success']({ message: '操作成功' })
          this.editModalVisible = false
          // 刷新列表
          this.getHistoryList()
        } else {
          this.$notification['error']({
            message: '提示',
            description: res.msg || '保存失败',
          })
        }
      } catch (err) {
        this.$notification['error']({
          message: '提示',
          description: '保存失败',
        })
      } finally {
        this.saving = false
      }
    },

    // 返回答题页
    handleReturn() {
      this.$emit('return')
    },
  },
}
</script>

<style lang="less" scoped>
.answer-history {
  padding: 0 20px 0 20px;

  .field {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;

    .field_l {
      font-size: 16px;
      font-weight: 600;
      color: #000000;
    }
  }

  .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
  }

  .history-list {
    max-height: calc(-263px + 100vh);
    overflow-y: auto;
    .history-item {
      background: #fff;
      border: 1px solid #e8e8e8;
      border-radius: 4px;
      padding: 16px;
      margin-bottom: 16px;

      .question-header {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        gap: 8px;
        margin-bottom: 12px;
      }

      .code {
        font-size: 14px;
        color: #8c8c8c;
        .type {
          padding: 0 8px;
          display: inline-block;
          line-height: 21px;
          text-align: center;
          margin-right: 5px;
          color: #3350c7;
          font-size: 12px;
          border-radius: 4px;
          border: 1px solid #3a5ff8;
        }
      }

      .title-l {
        font-size: 14px;
        font-weight: 600;
        color: #000;
      }

      .option-summary {
        border-radius: 4px;

        .summary-tag {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 4px;
          font-size: 13px;

          &.all-selected {
            background: #f6ffed;
            color: #52c41a;
            border: 1px solid #b7eb8f;
          }

          &.none-selected {
            background: #fff2f0;
            color: #ff4d4f;
            border: 1px solid #ffccc7;
          }
        }

        .selected-option {
          display: block;
          margin-bottom: 8px;
          padding: 8px;
          background: #f7f8fc;
          color: #595959;
          border-radius: 4px;
          font-size: 13px;
        }
      }
    }
  }
}
</style>

<!-- 弹窗样式（非 scoped，因为 a-modal 默认挂载到 body 下） -->
<style lang="less">
.answer-edit-modal {
  .edit-modal-content {
    .code {
      font-size: 14px;
      color: #8c8c8c;
      margin-bottom: 12px;
    }

    .title {
      display: flex;
      justify-content: space-between;
      font-size: 14px;
      font-weight: 600;
      color: #000000;
      margin: 12px 0;
    }

    .title-l {
      font-size: 14px;
      font-weight: 600;
      color: #000;
    }

    .example {
      font-size: 13px;
      margin-top: 4px;
      display: inline-block;
      color: #8c8c8c;
      .example-l {
        position: relative;
      }
    }

    .description {
      margin: 10px 0;
      font-size: 14px;
      color: #000000;
      .type {
        display: inline-block;
        width: 52px;
        line-height: 21px;
        text-align: center;
        margin-right: 5px;
        color: #3350c7;
        font-size: 12px;
        border-radius: 4px;
        border: 1px solid #3a5ff8;
      }
    }

    .option {
      .option-box {
        background: #f7f8fc;
        width: 100%;
        margin-bottom: 8px;
        font-size: 14px;

        .hot {
          color: #595959;
        }

        .ant-checkbox-wrapper {
          padding: 14px 4px;
          display: flex;
          align-items: center;
        }
      }

      .active {
        color: #595959;
      }
    }
    // 最后一行选项布局
    .option-row-last {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      gap: 48px;

      /deep/ .ant-checkbox-wrapper {
        margin-right: 0;
      }
    }

    .modal-footer {
      margin-top: 20px;
      text-align: right;

      .ant-btn {
        margin-left: 8px;
      }
    }
  }
}
</style>